import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import type { ApprovalChange, ApprovalChanges } from '@/admin/hooks/use-settlement'
import { humanise } from '@/admin/lib/display-helpers'
import { Badge } from '@/shared/components/ui/badge'
import { useFormatters } from '@/shared/lib/formatting'
import { cn } from '@/shared/lib/utils'

const CHANGE_TONE: Record<ApprovalChange['change'], string> = {
  added: 'bg-[var(--status-ok-bg)] text-[var(--status-ok)]',
  changed: 'bg-[var(--status-warn-bg)] text-[var(--status-warn)]',
  removed: 'bg-[var(--status-error-bg)] text-[var(--status-error)]',
}

/** Added / Changed / Removed marker for a row that moved since the last decision. */
export function ApprovalChangeBadge({
  change,
}: {
  change: ApprovalChange['change']
}) {
  const { t } = useTranslation()
  const label = {
    added: t('approvalChanges.added', 'Added'),
    changed: t('approvalChanges.changed', 'Changed'),
    removed: t('approvalChanges.removed', 'Removed'),
  }[change]
  return (
    <Badge
      className={cn(
        'h-4 rounded-[3px] px-1.5 text-[9px] uppercase tracking-[0.04em]',
        CHANGE_TONE[change],
      )}
    >
      {label}
    </Badge>
  )
}

/** The earlier value, struck through, shown before the current one. */
export function WasValue({ children }: { children: ReactNode }) {
  return (
    <span className="mr-1 text-[var(--ink-3)] line-through">{children}</span>
  )
}

/**
 * What moved since the last approval decision: the gated amounts, then the
 * settlement lines and payments added, changed or removed.
 */
export function ApprovalChangesSummary({
  changes,
}: {
  changes: ApprovalChanges
}) {
  const { t } = useTranslation()
  const { formatCurrency, formatDateTime } = useFormatters()
  const money = (value: string | null | undefined) =>
    value == null || value === '' || Number.isNaN(Number(value))
      ? '-'
      : formatCurrency(Number(value))

  const { since, amounts, lines, payments } = changes
  const context = {
    action: since.action_label,
    name: since.by,
    date: formatDateTime(since.at),
  }
  const nothing = !amounts.length && !lines.length && !payments.length

  return (
    <div
      data-testid="approval-changes"
      className="rounded-[8px] border-[0.5px] border-[var(--status-warn)] bg-[var(--bg-light)] px-4 py-3"
    >
      <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--ink-3)]">
        {t('approvalChanges.title', 'Changes since the last approval decision')}
      </p>
      <p className="mt-0.5 text-[11px] text-[var(--ink-3)]">
        {since.by
          ? t('approvalChanges.sinceBy', '{{action}} by {{name}}, {{date}}', context)
          : t('approvalChanges.since', '{{action}}, {{date}}', context)}
      </p>
      {nothing ? (
        <p className="mt-2 text-[12px] text-[var(--ink-2)]">
          {t('approvalChanges.none', 'Nothing has changed since then.')}
        </p>
      ) : (
        <ul className="mt-2 flex flex-col gap-1.5 text-[12px]">
          {amounts.map((amount) => (
            <li key={amount.key} className="flex items-baseline justify-between gap-3">
              <span className="text-[var(--ink-2)]">{amount.label}</span>
              <span className="tabular-nums text-[var(--ink)]">
                <WasValue>{money(amount.before)}</WasValue>
                {money(amount.after)}
              </span>
            </li>
          ))}
          {lines.map((line) => {
            const row = line.after ?? line.before
            const priceMoved = line.fields?.includes('extended_price')
            return (
              <li
                key={`line-${row?.uuid}`}
                className="flex items-baseline justify-between gap-3"
              >
                <span className="flex min-w-0 items-baseline gap-1.5 text-[var(--ink-2)]">
                  <ApprovalChangeBadge change={line.change} />
                  <span className={cn('truncate', line.change === 'removed' && 'line-through')}>
                    {row?.description}
                  </span>
                </span>
                <span className="shrink-0 tabular-nums text-[var(--ink)]">
                  {priceMoved && <WasValue>{money(line.before?.extended_price)}</WasValue>}
                  {money(row?.extended_price)}
                </span>
              </li>
            )
          })}
          {payments.map((payment) => {
            const row = payment.after ?? payment.before
            const was = payment.before
            return (
              <li
                key={`payment-${row?.uuid}`}
                className="flex items-baseline justify-between gap-3"
              >
                <span className="flex min-w-0 items-baseline gap-1.5 text-[var(--ink-2)]">
                  <ApprovalChangeBadge change={payment.change} />
                  <span>
                    {t('approvalChanges.payment', 'Payment')}
                    {' - '}
                    {payment.fields?.includes('status') && was && (
                      <WasValue>{humanise(was.status)}</WasValue>
                    )}
                    {humanise(row?.status)}
                  </span>
                </span>
                <span className="shrink-0 tabular-nums text-[var(--ink)]">
                  {payment.fields?.includes('amount') && (
                    <WasValue>{money(was?.amount)}</WasValue>
                  )}
                  {money(row?.amount)}
                </span>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
